import * as React from "react";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Tooltip from "@mui/material/Tooltip";
import { PdfFragment } from "../../../utils/download/downloadCollapsibleTable";

interface RowProps {
    row: IRow<any>;
    columns: IColumn<any>[];
    subColumns: IColumn<any>[];
    isOpen: boolean;
    onToggle: () => void;
}

function Row({ row, columns, subColumns, isOpen, onToggle }: RowProps) {
    return (
        <React.Fragment>
            <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
                <TableCell>
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={onToggle}
                    >
                        {isOpen ? (
                            <KeyboardArrowUpIcon />
                        ) : (
                            <KeyboardArrowDownIcon />
                        )}
                    </IconButton>
                </TableCell>
                {columns.map((column, i) => (
                    <TableCell key={i} align={column.align || "left"}>
                        {column.renderCustomCell?.({ row }) ?? row.data[column.field]}
                    </TableCell>
                ))}
            </TableRow>
            <TableRow>
                <TableCell
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                    colSpan={columns.length + 1}
                >
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="center" />
                                        {subColumns.map((column, i) => (
                                            <TableCell key={i}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 1,
                                                    }}
                                                >
                                                    {column.renderCustomHeader?.() ?? column.headerName}
                                                    {column.tooltipInfo && (
                                                        <Tooltip
                                                            title={
                                                                <span style={{ whiteSpace: 'pre-line' }}>
                                                                    {column.tooltipInfo}
                                                                </span>
                                                            }
                                                        >
                                                            <InfoOutlinedIcon fontSize="small"></InfoOutlinedIcon>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {row.subRowData.map((subrow, i) => (
                                        <TableRow key={i}>
                                            <TableCell align="center" />
                                            {subColumns.map((column, i) =>
                                                <TableCell key={i}>
                                                    {column.renderCustomCell?.({ row, subrow }) ?? subrow[column.field]}
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
}

export interface IColumn<T> {
    field: string;
    headerName: string;
    tooltipInfo?: string;
    align?: "inherit" | "left" | "center" | "right" | "justify";
    renderCustomHeader?: () => React.ReactNode | undefined;
    renderCustomCell?: (ctx: {
        row: IRow<T>;
        subrow?: T;
    }) => React.ReactNode | undefined;
    pdfRender?: (ctx: {
        row: IRow<T>;
        subrow?: T;
        value: any;
    }) => PdfFragment[] | undefined;
}
export interface IRow<T> {
    id: number | string;
    data: T;
    subRowData: T[];
}

interface CollapsibleTableProps<T> {
    rows: IRow<T>[];
    columns: IColumn<T>[];
    subColumns: IColumn<T>[];
    elevation?: number;
    expandedIds?: Set<string | number>;
    onToggleExpand?: (id: string | number) => void;
}

export default function CollapsibleTable({
    rows,
    columns,
    subColumns,
    elevation,
    expandedIds,
    onToggleExpand,
}: CollapsibleTableProps<any>) {
    const isExpandControlled = !!(expandedIds && onToggleExpand);
    const [internalExpanded, setInternalExpanded] = React.useState<Set<string | number>>(new Set());

    React.useEffect(() => {
        setInternalExpanded(prev => {
            const visible = new Set(rows.map(r => r.id));
            const next = new Set(Array.from(prev).filter(id => visible.has(id)));
            return next;
        });
    }, [rows]);

    const isRowOpen = (id: string | number) =>
        isExpandControlled ? !!expandedIds?.has(id) : internalExpanded.has(id);

    const toggleRow = (id: string | number) => {
        if (isExpandControlled) onToggleExpand?.(id);
        else {
            setInternalExpanded(prev => {
                const next = new Set(prev);
                next.has(id) ? next.delete(id) : next.add(id);
                return next;
            });
        }
    };

    return (
        <TableContainer component={Paper} elevation={elevation || 1}>
            <Table aria-label="collapsible table">
                {columns.some((column) => column.headerName || column.renderCustomHeader) && (
                    <TableHead>
                        <TableRow>
                            <TableCell align="center" />
                            {columns.map((column, i) => (
                                <TableCell key={i}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                        }}
                                    >
                                        <Box sx={{ textAlign: "center" }}>
                                            {column.renderCustomHeader?.() ?? column.headerName}
                                        </Box>

                                        {column.tooltipInfo && (
                                            <Tooltip
                                                title={
                                                    <span style={{ whiteSpace: 'pre-line' }}>
                                                        {column.tooltipInfo}
                                                    </span>
                                                }
                                            >
                                                <InfoOutlinedIcon fontSize="small"></InfoOutlinedIcon>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                )}
                <TableBody>
                    {rows.map((row) => (
                        <Row
                            key={row.id}
                            row={row}
                            columns={columns}
                            subColumns={subColumns}
                            isOpen={isRowOpen(row.id)}
                            onToggle={() => toggleRow(row.id)}
                        />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
